import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Calendar, GraduationCap, ShieldCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import AccountBillingPanel from "../components/AccountBillingPanel";

/**
 * AccountSettings — read-only summary of the teacher's account.
 *
 * Lives at `/account`. Surfaces the basics they actually need: email,
 * signup date, tier badge, admin flag. Classroom profile opens here
 * (Phase 115.3 — moved out of the rail footer into the Account page).
 */
const AccountSettings = () => {
  const { user, openProfileModal } = useAuth();

  if (!user) {
    return (
      <div className="min-h-screen bg-[#f4f5f7] grid place-items-center px-6">
        <div className="ae-card p-8 max-w-md text-center">
          <p className="font-extrabold text-xl text-[#1c2d3d]">Sign in required</p>
          <p className="mt-3 text-[#4a6984] font-medium">
            Account settings are visible once you&apos;re signed in.
          </p>
          <Link
            to="/"
            data-testid="account-back-home"
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#4a6984] hover:text-[#1c2d3d]"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to home
          </Link>
        </div>
      </div>
    );
  }

  const created = user.created_at
    ? new Date(user.created_at).toLocaleDateString("en-CA", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div
      className="bg-[#f4f5f7] text-[#4a6984]"
      data-testid="account-settings-page"
    >
      <main className="py-8 max-w-2xl mx-auto px-6">
        <p
          data-testid="account-kicker"
          className="font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]/70"
        >
          Account
        </p>
        <h1
          data-testid="account-heading"
          className="mt-3 font-black text-3xl sm:text-4xl text-[#1c2d3d] tracking-tight"
        >
          Account settings
        </h1>

        <div className="mt-8 ae-card p-6 md:p-7 shadow-sm">
          <div className="flex items-center gap-4">
            {user.picture ? (
              <img
                src={user.picture}
                alt=""
                className="h-14 w-14 rounded-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              <span className="h-14 w-14 rounded-full bg-[#96ded1] text-[#1c2d3d] grid place-items-center font-extrabold text-lg">
                {(user.name || user.email || "?").slice(0, 2).toUpperCase()}
              </span>
            )}
            <div className="min-w-0">
              <p
                data-testid="account-name"
                className="font-extrabold text-lg text-[#1c2d3d] truncate"
              >
                {user.name || "Teacher"}
              </p>
              <p
                data-testid="account-email"
                className="text-sm text-[#4a6984]/80 font-medium truncate inline-flex items-center gap-1.5"
              >
                <Mail className="h-3.5 w-3.5" /> {user.email}
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {created && (
              <div className="rounded-lg border border-[#4a6984]/10 p-4 bg-[#f4f5f7]/60">
                <p className="font-bold uppercase tracking-[0.18em] text-xs text-[#4a6984]/60 inline-flex items-center gap-1.5">
                  <Calendar className="h-3 w-3" /> Member since
                </p>
                <p
                  data-testid="account-created"
                  className="mt-1.5 font-semibold text-[#1c2d3d]"
                >
                  {created}
                </p>
              </div>
            )}
            {user.is_admin && (
              <div className="rounded-lg border border-[#96ded1]/40 p-4 bg-[#96ded1]/10">
                <p className="font-bold uppercase tracking-[0.18em] text-xs text-[#1c2d3d]/70 inline-flex items-center gap-1.5">
                  <ShieldCheck className="h-3 w-3" /> Role
                </p>
                <p
                  data-testid="account-role"
                  className="mt-1.5 font-extrabold text-[#1c2d3d]"
                >
                  Admin
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Billing & plan — Phase 141. Surfaces current plan, status,
            next renewal / trial end, manage-in-Stripe + a compact
            "All plans" tile row so a teacher can switch without
            leaving the page. */}
        <AccountBillingPanel />

        {/* Classroom profile — moved here from the rail footer in
            Phase 115.3. The profile modal carries everything else
            (subjects taught, board, focus areas) so a single button
            opens the same UX the avatar dropdown used to. */}
        <div className="mt-6 ae-card p-5 sm:p-6 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-[200px]">
            <p className="font-extrabold text-base text-[#1c2d3d] inline-flex items-center gap-2">
              <GraduationCap className="h-4 w-4 text-[#4a6984]" />
              Classroom profile
            </p>
            <p className="mt-1 text-sm text-[#4a6984] font-medium">
              Subjects you teach, board, focus areas. Used to tailor lesson
              suggestions and the daily Skill in the Wild.
            </p>
          </div>
          <button
            type="button"
            onClick={openProfileModal}
            data-testid="account-open-classroom-profile"
            className="inline-flex items-center gap-2 rounded-md bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white px-4 py-2 text-sm font-bold transition-colors"
          >
            <GraduationCap className="h-4 w-4" />
            Edit profile
          </button>
        </div>

        <p className="mt-6 text-sm text-[#4a6984]/70 font-medium">
          Need to manage Google Drive or sign out? Use the left rail.
        </p>
      </main>
    </div>
  );
};

export default AccountSettings;
